import React, { useEffect, useMemo, useState } from 'react';
import LayoutComponent from '../BaseScreen';
import { useData } from '../../Context/DataContext';
import { useNavigate } from 'react-router-dom';
import CensusFilterComponent from '../../Components/SortAndFilter/CensusFilterComponent';
import SelectInputComponent from '../../Components/Inputs/SelectInputComponent';
import CensusFilterOptions from '../../Options/CensusFilterOptions';
import { MoreHorizontal } from 'react-feather';

const CensusScreen = () => {
  const navigation = useNavigate()
  const { currentPatients, getCurrentPatients, updatePatientDetails, startDateCensus, endDateCensus } = useData();

  // Default to a value that actually exists in IntellasureDB (`census.auth_status`)
  const [statusFilter, setStatusFilter] = useState<string>("All")
  const [facilityFilter, setFacilityFilter] = useState<string>("All")
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(25)
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({})

  useEffect(() => {
    getCurrentPatients();
    const savedStatusFilter = localStorage.getItem('currentStatusFilter');
    if (savedStatusFilter) {
      const saved = JSON.parse(savedStatusFilter);
      if (typeof saved === 'string' && CensusFilterOptions.includes(saved)) setStatusFilter(saved);
    }

  }, [startDateCensus, endDateCensus])

  const redirectToPatientPage = (patient: any) => {
    updatePatientDetails(patient)
    navigation(`/census/${patient.policy_no}`)
  }

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    localStorage.setItem('currentStatusFilter', JSON.stringify(value))
    setPage(1);
  }

  const handleFacilityFilterChange = (value: string) => {
    setFacilityFilter(value);
    setPage(1);
  }

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const formatDateShort = (value: any) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const calcAge = (dob: any) => {
    if (!dob) return '';
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
    return age;
  };

  const facilityOptions = ['All', 'Axis', 'Beachside'];

  const allRows = useMemo(() => {
    const rows: any[] = [];
    if (!currentPatients) return rows;
    Object.keys(currentPatients).forEach((facility) => {
      const list = currentPatients?.[facility];
      if (!Array.isArray(list)) return;
      list.forEach((p: any) => rows.push({ ...p, facility }));
    });
    return rows;
  }, [currentPatients]);

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return allRows.filter((r: any) => {
      if (facilityFilter !== 'All' && String(r.facility || '').toLowerCase() !== facilityFilter.toLowerCase()) return false;
      if (statusFilter !== 'All' && String(r.auth_status || '') !== statusFilter) return false;
      if (!q) return true;
      const haystack = [
        `${r.first_name || ''} ${r.last_name || ''}`,
        r.policy_no,
        r.insurance_name,
        r.level_of_care,
        r.cpt_code,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allRows, facilityFilter, statusFilter, searchTerm]);

  const stats = useMemo(() => {
    const base = facilityFilter === 'All'
      ? allRows
      : allRows.filter((r: any) => String(r.facility || '').toLowerCase() === facilityFilter.toLowerCase());

    const counts: Record<string, number> = { Approved: 0, Pending: 0, 'Not Required': 0 };
    base.forEach((r: any) => {
      const s = String(r.auth_status || '');
      if (counts[s] !== undefined) counts[s] += 1;
    });
    return {
      total: base.length,
      approved: counts['Approved'],
      pending: counts['Pending'],
      notRequired: counts['Not Required'],
    };
  }, [allRows, facilityFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const pageRows = filteredRows.slice(startIdx, startIdx + pageSize);

  const statusPillClass = (status: string) => {
    if (status === 'Approved') return 'bg-green-500/15 text-green-300 border-green-500/30';
    if (status === 'Pending') return 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30';
    if (status === 'Not Required') return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
    return 'bg-gray-500/15 text-gray-300 border-gray-500/30';
  };

  const toggleSelectAllVisible = (checked: boolean) => {
    const next = { ...selectedIds };
    pageRows.forEach((r: any) => {
      const key = String(r.intella_claim_id || r.policy_no || '');
      if (!key) return;
      next[key] = checked;
    });
    setSelectedIds(next);
  };

  const toggleSelectOne = (key: string, checked: boolean) => {
    setSelectedIds((prev) => ({ ...prev, [key]: checked }));
  };

  const exportCsv = () => {
    const headers = [
      'first_name',
      'last_name',
      'facility',
      'level_of_care',
      'cpt_code',
      'policy_no',
      'insurance_name',
      'auth_status',
      'days_left',
      'start_date',
      'end_date',
      'date_of_birth',
    ];

    const escape = (v: any) => {
      const s = String(v ?? '');
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const lines = [
      headers.join(','),
      ...filteredRows.map((r: any) =>
        headers
          .map((h) => {
            if (h === 'start_date' || h === 'end_date' || h === 'date_of_birth') return escape(formatDateShort(r[h]));
            return escape(r[h]);
          })
          .join(',')
      ),
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `census_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <LayoutComponent
      header={
          <div className="w-full h-full bg-dark-card border border-dark-border rounded-xl px-4 flex items-center justify-between">
            <div className="flex flex-col">
              <div className="text-white font-semibold text-lg">Census</div>
              <div className="text-xs text-gray-400 -mt-0.5">All patient information in one place</div>
            </div>
            <div className="flex items-center gap-3 w-[520px] max-w-[60%]">
              <div className="flex-1">
                <div className="rounded-xl flex flex-row items-center w-full bg-dark-input border border-dark-border px-3 py-2">
                  <input
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="text-sm w-full bg-transparent text-white placeholder-gray-500 focus:outline-none"
                    placeholder="Search patient, policy, insurance..."
                  />
                </div>
              </div>
              <button
                onClick={exportCsv}
                className="px-3 py-2 rounded-xl bg-accent-teal text-black text-sm font-semibold hover:opacity-90"
              >
                Export
              </button>
            </div>
          </div>
      }
      content={
        <div className="w-full h-full overflow-hidden px-4 pb-6 flex flex-col">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="text-xs text-gray-400">Current Census Total</div>
              <div className="mt-1 text-2xl font-semibold text-white">{stats.total}</div>
            </div>
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="text-xs text-gray-400">Pending</div>
              <div className="mt-1 text-2xl font-semibold text-yellow-300">{stats.pending}</div>
            </div>
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="text-xs text-gray-400">Approved</div>
              <div className="mt-1 text-2xl font-semibold text-green-300">{stats.approved}</div>
            </div>
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="text-xs text-gray-400">Not Required</div>
              <div className="mt-1 text-2xl font-semibold text-blue-300">{stats.notRequired}</div>
            </div>
          </div>

          {/* Filters */}
          <div className="mt-4 bg-dark-card border border-dark-border rounded-xl p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CensusFilterComponent />
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400 font-medium">Facility</span>
                <SelectInputComponent
                  options={facilityOptions}
                  selectedValue={facilityFilter}
                  handleOptionClick={handleFacilityFilterChange}
                  placeholder="All"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400 font-medium">Status</span>
                <SelectInputComponent
                  options={CensusFilterOptions}
                  selectedValue={statusFilter}
                  handleOptionClick={handleStatusFilterChange}
                  placeholder="All"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">Rows</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                className="px-3 py-1.5 bg-dark-input border border-dark-border text-sm rounded-xl text-white focus:outline-none focus:border-accent-teal transition-colors cursor-pointer"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="mt-4 bg-dark-card border border-dark-border rounded-xl overflow-hidden flex flex-col flex-1 min-h-0">
            <div className="overflow-auto flex-1">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-dark-card z-10">
                  <tr className="text-left text-gray-400 border-b border-dark-border">
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                        className="h-4 w-4 accent-accent-teal"
                      />
                    </th>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Facility</th>
                    <th className="px-4 py-3 font-medium">LOC</th>
                    <th className="px-4 py-3 font-medium">Start</th>
                    <th className="px-4 py-3 font-medium">Age</th>
                    <th className="px-4 py-3 font-medium">DOB</th>
                    <th className="px-4 py-3 font-medium">Insurance</th>
                    <th className="px-4 py-3 font-medium">Policy</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Days left</th>
                    <th className="px-4 py-3 font-medium w-12" />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r: any) => {
                    const key = String(r.intella_claim_id || r.policy_no || '');
                    const checked = !!selectedIds[key];
                    return (
                      <tr
                        key={key}
                        className="border-b border-dark-border hover:bg-dark-card-hover cursor-pointer"
                        onClick={() => redirectToPatientPage(r)}
                      >
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => toggleSelectOne(key, e.target.checked)}
                            className="h-4 w-4 accent-accent-teal"
                          />
                        </td>
                        <td className="px-4 py-3 text-white font-medium">
                          {r.first_name} {r.last_name}
                        </td>
                        <td className="px-4 py-3 text-gray-300">{r.facility}</td>
                        <td className="px-4 py-3 text-gray-300">{r.level_of_care}{r.cpt_code ? ` (${r.cpt_code})` : ''}</td>
                        <td className="px-4 py-3 text-gray-300">{formatDateShort(r.start_date)}</td>
                        <td className="px-4 py-3 text-gray-300">{calcAge(r.date_of_birth)}</td>
                        <td className="px-4 py-3 text-gray-300">{formatDateShort(r.date_of_birth)}</td>
                        <td className="px-4 py-3 text-gray-300">{r.insurance_name}</td>
                        <td className="px-4 py-3 text-gray-300">{r.policy_no}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full border text-xs ${statusPillClass(String(r.auth_status || ''))}`}>
                            {r.auth_status || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-300">{r.days_left ?? ''}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="p-2 rounded-lg hover:bg-dark-input border border-transparent hover:border-dark-border"
                            onClick={() => redirectToPatientPage(r)}
                            aria-label="Open"
                          >
                            <MoreHorizontal size={16} className="text-gray-400" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-4 py-10 text-center text-gray-400">
                        No results.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 flex items-center justify-between text-sm text-gray-400">
              <div>
                Showing {filteredRows.length === 0 ? 0 : startIdx + 1} to {Math.min(startIdx + pageSize, filteredRows.length)} of {filteredRows.length} entries
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1.5 rounded-lg border border-dark-border hover:bg-dark-input disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                >
                  Previous
                </button>
                <div className="px-2 text-gray-300">
                  {safePage} / {totalPages}
                </div>
                <button
                  className="px-3 py-1.5 rounded-lg border border-dark-border hover:bg-dark-input disabled:opacity-50"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}

export default CensusScreen;